import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilteredMedia from '../FilteredMedia/FilteredMedia';
import { Skeleton, Typography } from '@nackle/paper';
import './styles.less';

/**
 * Large banner to show title and description and a video/image
 * @param {string} title title of banner
 * @param {string?} description description of banner. limited to 4 lines
 * @param {string?} videoUrl src url of video to play in background (supercedes image prop)
 * @param {string?} image src url of image to show in background
 */
const LargeBanner = ( { title, description, videoUrl, image, className, loading, children } ) => {

    return <Banner size='lg' { ...{ videoUrl, image, className } }>
        <div className='text-content'>
            { loading && !title ? <Skeleton active paragraph={ { rows: 4, width: '100%' } } /> :
                <>
                    <h1 className="title _header">{ title }</h1> 
                    <Typography.Paragraph 
                        className="description _title text-no" 
                        ellipsis={ { rows: 4, expandable: false } }
                        
                    >
                        <span dangerouslySetInnerHTML={ { __html: description } } />
                    </Typography.Paragraph>
                </> }
        </div>

        { children && 
            <div className='additional-content'>
                { children }
            </div> 
        }
    </Banner>;
};


const Banner = ( { size, children, videoUrl, image, className } ) => {

    const [ imageLoaded, setImageLoaded ] = useState( false );

    const videoPlayerRef = useRef();
    const videoPlayerRefBkg = useRef();

    const imageStyle = useMemo( () => {
        if ( imageLoaded ) {
            return image ? { backgroundImage: `url( ${ image } )` } : {};
        } else {
            return;
        }
    }, [ image, imageLoaded ] );

    const handleImageLoad = () => {
        setImageLoaded( true );
    };

    useEffect( () => {
        if ( videoUrl && videoPlayerRef?.current && videoPlayerRefBkg?.current ) {

            videoPlayerRef.current.addEventListener( 'loadeddata', function() {
                // Video is loaded and can be played
                setTimeout( () => {
                    videoPlayerRef.current?.play();
                    videoPlayerRefBkg.current?.play();
                }, 500 );
            }, false );
        }
    }, [ videoUrl, videoPlayerRef ] );

    return (
            
        <div className={ classNames( 'banner', `banner-${ size }`, { 'video-banner': videoUrl, 'image-banner': !videoUrl }, className ) }>
            
            { videoUrl && 
                <FilteredMedia>
                    <div className="video-container">
                        <video 
                            ref={ videoPlayerRefBkg }
                            loop
                            muted 
                            // autoPlay
                            playsInline
                            className='bkg-vid'
                        >
                            <source 
                                src={ videoUrl } 
                                type="video/mp4" 
                            />
                        </video>
                        <video 
                            ref={ videoPlayerRef }
                            loop
                            muted 
                            // autoPlay
                            playsInline
                            className='primary-vid'
                        >
                            <source 
                                src={ videoUrl } 
                                type="video/mp4" 
                            />
                        </video>

                        <div className="banner-content">
                            { children }
                        </div>
                    </div>
                </FilteredMedia>
            }

            { !videoUrl && 
                <>
                    { image &&
                        <img 
                            style={ { display: 'none' } }
                            onLoad={ handleImageLoad }
                            src={ image }
                        />
                    }
                    <FilteredMedia type="image" style={ imageStyle }>
                        <div className="banner-content">
                            { children }
                        </div>
                    </FilteredMedia> 
                </>
            }
        </div>    
    );

};

Banner.Video = LargeBanner;
Banner.Large = LargeBanner;
// more banenr specifications...


export default Banner;